import pick from 'lodash.pick';
import { Fragment, useState, FunctionComponent } from 'react';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay/legacy';
import { connect } from 'react-redux';
import {
    selectBankAccount,
    bankAccountDeleted,
    bankAccountFormCanceled,
    verifyBankAccount,
    addBankAccount,
} from '../../actions/checkoutPaymentActions';
import { ACHFields } from '../../helpers/checkoutForms';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { paymentMethodIsPermissible } from '../helpers/paymentMethodIsPermissible';
import { STATES } from '../../constants/checkoutConstants';
import { useAppDispatch } from '../../reducers/root';

// components
import CheckoutBankAccountList, { OnSelectEventType } from './CheckoutBankAccountList';
import CheckoutConfirmModal from '../../shared/CheckoutConfirmModal';

import { CheckoutBankAccountListWrapper_viewer$data } from './__generated__/CheckoutBankAccountListWrapper_viewer.graphql';
import { CheckoutBankAccountListWrapper_buyer$data } from './__generated__/CheckoutBankAccountListWrapper_buyer.graphql';
import { CheckoutBankAccountListWrapper_transaction$data } from './__generated__/CheckoutBankAccountListWrapper_transaction.graphql';

import styles from '../creditCard/styles/CheckoutCreditCardList.scss';

type OnBankAccountDeletedArg = {
    bankAccountState: string;
    paymentMethodToken?: string;
    isSelectedBankAccountDeleted: boolean;
};

type Props = {
    viewer: CheckoutBankAccountListWrapper_viewer$data;
    buyer: CheckoutBankAccountListWrapper_buyer$data;
    transaction: CheckoutBankAccountListWrapper_transaction$data;
    relay: RelayProp;
    paymentMethodToken?: string;
    onSelectBankAccount: (token: string, paymentProcessor: string) => void;
    onBankAccountDeleted: (arg1: OnBankAccountDeletedArg) => void;
    onBankAccountFormCanceled: () => void;
    checkoutPaymentErrors: ObjectType;
    onValidationUpdate: () => void;
    onValidationError: () => void;
    expandableBankAccountList?: boolean;
    onBankAccountMethodSelect?: () => void;
    deselectBankAccounts?: boolean;
    onVerifyBankAccount: typeof verifyBankAccount;
    shippingAddressId: string;
};

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType[number];

type BuyerWallet = NonNullable<CheckoutBankAccountListWrapper_buyer$data['wallet']>;
type PaymentMethod = NonNullable<
    ArrayElement<NonNullable<BuyerWallet['paymentMethods']>>
>['paymentMethod'];

type ACHPaymentMethod = Extract<PaymentMethod, { __typename: 'ACH' }>;

function isACHPaymentMethod(
    paymentMethod?: PaymentMethod | null
): paymentMethod is ACHPaymentMethod {
    return !!paymentMethod && paymentMethod.__typename === 'ACH';
}

type MutationResponseType = {
    userDeletePaymentMethod: {
        user: CheckoutBankAccountListWrapper_buyer$data;
    };
};

const CheckoutBankAccountListWrapper: FunctionComponent<Props> = ({
    relay,
    viewer,
    buyer,
    transaction,
    paymentMethodToken,
    onSelectBankAccount,
    checkoutPaymentErrors,
    onValidationUpdate,
    onValidationError,
    expandableBankAccountList = true,
    onBankAccountMethodSelect = () => {},
    onBankAccountFormCanceled,
    onBankAccountDeleted,
    onVerifyBankAccount,
    shippingAddressId,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedBankAccountToken, setSelectedBankAccountToken] = useState('');
    const [showStripeVerifyMessage, setShowStripeVerifyMessage] = useState(false);
    const dispatch = useAppDispatch();

    const onSelect: OnSelectEventType = (event, selectedToken, isSelected): void => {
        if (isSelected) {
            return;
        }

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const walletPaymentMethods = buyer?.wallet?.paymentMethods || [];
        const walletPaymentMethod = selectedToken
            ? walletPaymentMethods.find(
                  ({ paymentMethod }: $TSFixMe) =>
                      isACHPaymentMethod(paymentMethod) && paymentMethod.token === selectedToken
              ) || null
            : null;
        const bankAccount = isACHPaymentMethod(walletPaymentMethod?.paymentMethod)
            ? walletPaymentMethod?.paymentMethod
            : null;
        const isPermissibleBankAccount = paymentMethodIsPermissible(
            walletPaymentMethod,
            shippingAddressId
        );

        onBankAccountMethodSelect();

        if (!isPermissibleBankAccount) {
            if (viewer?.stripeIsEnabled) {
                setShowStripeVerifyMessage(true);
                dispatch(addBankAccount());
            } else {
                const verifyBankAccountForm = pick(bankAccount, ACHFields);
                onVerifyBankAccount({
                    token: selectedToken,
                    verifyBankAccountForm: {
                        ...verifyBankAccountForm,
                        ownershipType: 'personal',
                    },
                });
            }
        } else if (selectedToken) {
            onSelectBankAccount(selectedToken, bankAccount?.paymentProcessor || '');
        }
    };

    const onClose = (): void => {
        setIsOpen(false);
    };

    const onDelete = (token: string): void => {
        setIsOpen(true);
        setSelectedBankAccountToken(token);
    };

    const onCancel = (): void => {
        onBankAccountFormCanceled();
    };

    const onDischarge = (): void => {
        // trigger on confirmation modal discharge
        onClose();
    };

    const deleteBankAccount = async (): Promise<void> => {
        const isMobile = false;
        let paymentMethod: ACHPaymentMethod | null = null;
        let nextSelectableBankAccount: ACHPaymentMethod | null = null;
        const paymentMethods = buyer?.wallet?.paymentMethods || [];
        for (const walletMethod of paymentMethods) {
            const walletPaymentMethod = walletMethod?.paymentMethod || null;
            if (
                isACHPaymentMethod(walletPaymentMethod) &&
                !nextSelectableBankAccount &&
                walletPaymentMethod?.token !== selectedBankAccountToken
            ) {
                nextSelectableBankAccount = walletPaymentMethod;
            }
            if (
                isACHPaymentMethod(walletPaymentMethod) &&
                walletPaymentMethod?.token === selectedBankAccountToken
            ) {
                paymentMethod = walletPaymentMethod;
            }
        }
        const nextSelectableBankAccountToken = nextSelectableBankAccount?.token || '';
        const walletPaymentMethodToken = (paymentMethod && paymentMethod.token) || '';

        try {
            const mutationResponse = (await CheckoutMutationWrapper.commit(
                'UserDeletePaymentMethodMutation',
                relay.environment,
                {
                    userId: buyer?.serviceId,
                    paymentMethodType: 'ACH',
                    paymentMethodKey: walletPaymentMethodToken,
                },
                isMobile
            )) as MutationResponseType;

            const { user } = mutationResponse.userDeletePaymentMethod;
            const hasBankAccounts = user?.wallet?.hasBankAccounts;
            const isSelectedBankAccountDeleted = paymentMethodToken === selectedBankAccountToken;

            onBankAccountDeleted({
                bankAccountState: hasBankAccounts ? STATES.STATE_SAVED : STATES.STATE_NEW,
                paymentMethodToken: isSelectedBankAccountDeleted
                    ? nextSelectableBankAccountToken
                    : paymentMethodToken,
                isSelectedBankAccountDeleted,
            });

            onClose();
        } catch (error) {
            logCheckoutError(error, 'ach');
        }
    };

    const onConfirm = (): void => {
        // trigger on confirmation modal submission
        deleteBankAccount();
    };

    const actions = { onDelete, onCancel };
    const bankAccountProps = {
        viewer,
        buyer,
        checkoutPaymentErrors,
        onValidationUpdate,
        onValidationError,
    };

    return (
        <Fragment>
            {showStripeVerifyMessage && (
                <div className={styles.stripeVerifyMessage}>
                    You've selected a new shipping address. For security purposes, you'll need to
                    re-enter your payment information.
                </div>
            )}
            {!showStripeVerifyMessage && (
                <CheckoutBankAccountList
                    {...bankAccountProps}
                    onSelect={onSelect}
                    actions={actions}
                    expandableBankAccountList={expandableBankAccountList}
                    transaction={transaction}
                />
            )}
            {isOpen && (
                <CheckoutConfirmModal
                    isOpen={isOpen}
                    onDischarge={onDischarge}
                    onConfirm={onConfirm}
                    onClose={onClose}
                />
            )}
        </Fragment>
    );
};

type ConnectTransactionType = {
    paymentMethodToken: string;
    shippingAddressId: string;
};

type ConnectPaymentType = {
    deselectBankAccounts: boolean;
};

type ConnectType = {
    payment: ConnectPaymentType;
    transaction: ConnectTransactionType;
};

const ConnectedCheckoutBankAccountListWrapper = connect(
    ({ transaction, payment }: ConnectType) => ({
        paymentMethodToken: transaction.paymentMethodToken,
        deselectBankAccounts: payment.deselectBankAccounts,
        shippingAddressId: transaction.shippingAddressId,
    }),
    {
        onSelectBankAccount: selectBankAccount,
        onBankAccountDeleted: bankAccountDeleted,
        onBankAccountFormCanceled: bankAccountFormCanceled,
        onVerifyBankAccount: verifyBankAccount,
    }
)(CheckoutBankAccountListWrapper);

export default createFragmentContainer(ConnectedCheckoutBankAccountListWrapper, {
    viewer: graphql`
        fragment CheckoutBankAccountListWrapper_viewer on Viewer {
            ...CheckoutBankAccountList_viewer
            stripeIsEnabled: featureFlag(feature: "stripeCheckout")
        }
    `,
    buyer: graphql`
        fragment CheckoutBankAccountListWrapper_buyer on User {
            ...CheckoutBankAccountList_buyer
            serviceId
            wallet {
                hasBankAccounts
                paymentMethods {
                    paymentMethod {
                        __typename
                        ... on ACH {
                            token
                            paymentProcessor
                        }
                    }
                    ...paymentMethodIsPermissible_paymentMethod
                }
            }
        }
    `,
    transaction: graphql`
        fragment CheckoutBankAccountListWrapper_transaction on Transaction {
            ...CheckoutBankAccountList_transaction
        }
    `,
});
